import React from 'react';
import PropTypes from 'prop-types';
import { LuBookOpen, LuCircleCheck, LuFileText, LuListOrdered, LuPlay, LuSearch } from 'react-icons/lu';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { typed } from '../engine/stage';
import { CHAPTER_ICONS, GROUPS } from '../chapters';

const Backdrop = ({ children }) => (
  <div className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-[#f4f6f9] dark:tw-bg-[#14171c]">
    <span className="tw-absolute -tw-left-24 -tw-top-24 tw-h-[420px] tw-w-[420px] tw-rounded-full tw-bg-teal-400/25 tw-blur-3xl" />
    <span className="tw-absolute -tw-bottom-32 tw-right-0 tw-h-[460px] tw-w-[460px] tw-rounded-full tw-bg-indigo-500/20 tw-blur-3xl" />
    <span className="tw-absolute tw-left-1/2 tw-top-1/3 tw-h-[300px] tw-w-[300px] tw-rounded-full tw-bg-cyan-400/10 tw-blur-3xl" />
    <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-20 tw-text-center">{children}</div>
  </div>
);

Backdrop.propTypes = { children: PropTypes.node };

const Rise = ({ show, delay = 0, children, className = '' }) => (
  <div style={{ transitionDelay: `${delay}ms` }} className={`tw-transition-all tw-duration-700 ${show ? 'tw-translate-y-0 tw-opacity-100' : 'tw-translate-y-4 tw-opacity-0'} ${className}`}>
    {children}
  </div>
);

Rise.propTypes = { show: PropTypes.bool, delay: PropTypes.number, children: PropTypes.node, className: PropTypes.string };

const CARD = 'tw-rounded-3xl tw-border tw-border-white tw-bg-white/80 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06]';

/** Intro (the groups and their topics), how to use the guide, and the closing card. */
const TitleScene = ({ state, p }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`userGuide.${key}`, vars);

  if (state.variant === 'howto') {
    const query = typed(label('stage.howtoQuery'), p, 0.25, 0.4);
    const step = p < 0.25 ? 1 : p < 0.6 ? 2 : 3;
    const Callout = ({ n, active, children }) => (
      <span className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition-all tw-duration-500 ${active ? 'tw-bg-teal-500 tw-text-white tw-shadow-lg tw-shadow-teal-500/30' : 'tw-bg-white/80 tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300'}`}>
        <span className={`tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-text-xs ${active ? 'tw-bg-white tw-text-teal-700' : 'tw-bg-slate-200 tw-text-slate-600 dark:tw-bg-white/15 dark:tw-text-slate-200'}`}>{n}</span>
        {children}
      </span>
    );
    Callout.propTypes = { n: PropTypes.number, active: PropTypes.bool, children: PropTypes.node };
    return (
      <Backdrop>
        <p className="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('stage.howtoTitle')}</p>
        <div className="tw-mt-6 tw-flex tw-gap-3">
          <Callout n={1} active={step === 1}>{label('stage.howtoTopics')}</Callout>
          <Callout n={2} active={step === 2}>{label('stage.howtoSearch')}</Callout>
          <Callout n={3} active={step === 3}>{label('stage.howtoSteps')}</Callout>
        </div>
        <div className="tw-mt-8 tw-flex tw-w-[1000px] tw-gap-4 tw-text-left">
          <div className={`tw-w-[280px] tw-p-4 ${CARD} ${step <= 2 ? 'tw-ring-[3px] tw-ring-teal-400' : ''}`}>
            <span className={`tw-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm dark:tw-border-white/10 dark:tw-bg-white/5 ${step === 2 ? 'tw-ring-2 tw-ring-teal-500/50' : ''}`}>
              <LuSearch aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
              <span className={query ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400'}>{query || label('player.searchPlaceholder')}</span>
            </span>
            <div className="tw-mt-3 tw-space-y-1.5">
              {(query ? ['leaveRequest', 'leaveList'] : ['dashboard', 'tasks', 'files', 'leaveRequest', 'expenseRequest']).map((key, index) => {
                const Icon = CHAPTER_ICONS[key];
                return (
                  <span key={key} className={`tw-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-xl tw-px-2.5 tw-text-[13px] tw-font-semibold ${index === 0 && query ? 'tw-bg-teal-500/15 tw-text-teal-900 dark:tw-text-teal-100' : 'tw-text-slate-700 dark:tw-text-slate-200'}`}>
                    <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" />
                    {label(`chapters.${key}`)}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="tw-flex-1 tw-space-y-3">
            <div className={`tw-grid tw-h-[230px] tw-place-items-center tw-rounded-3xl tw-bg-gradient-to-br tw-from-teal-500 tw-to-indigo-600 tw-shadow-lg`}>
              <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600"><LuPlay aria-hidden="true" className="tw-ml-1 tw-h-8 tw-w-8" /></span>
            </div>
            <div className={`tw-p-4 ${CARD} ${step === 3 ? 'tw-ring-[3px] tw-ring-teal-400' : ''}`}>
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100"><LuListOrdered aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" />{label('player.steps')}</p>
              {[1, 2, 3].map((n) => (
                <p key={n} className={`tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1 tw-text-[13px] ${n === 2 ? 'tw-bg-teal-500/10 tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
                  <span className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-slate-100 tw-text-[10px] tw-font-bold dark:tw-bg-white/10">{n}</span>
                  {label(`stage.howtoStep${n}`)}
                </p>
              ))}
            </div>
          </div>
        </div>
      </Backdrop>
    );
  }

  if (state.variant === 'end') {
    return (
      <Backdrop>
        <Rise show>
          <span className="tw-mx-auto tw-mb-6 tw-grid tw-h-24 tw-w-24 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-emerald-400 tw-to-teal-600 tw-text-white tw-shadow-2xl tw-shadow-emerald-500/30">
            <LuCircleCheck className="tw-h-12 tw-w-12" />
          </span>
          <p className="tw-text-5xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('stage.endTitle')}</p>
          <p className="tw-mx-auto tw-mt-4 tw-max-w-3xl tw-text-xl tw-text-slate-600 dark:tw-text-slate-300">{label('stage.endText')}</p>
        </Rise>
        <Rise show={p > 0.25} delay={100}>
          <div className="tw-mt-10 tw-flex tw-items-center tw-gap-4">
            <span className="tw-inline-flex tw-h-12 tw-items-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-px-6 tw-text-lg tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
              <LuPlay className="tw-h-5 tw-w-5" />
              {label('card.title')}
            </span>
            <span className="tw-inline-flex tw-h-12 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/80 tw-px-6 tw-text-lg tw-font-semibold tw-text-slate-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
              <LuFileText className="tw-h-5 tw-w-5" />
              {t('dashboard.links.manual.title')}
            </span>
          </div>
        </Rise>
      </Backdrop>
    );
  }

  const groups = GROUPS.filter((group) => group.key !== 'end');
  return (
    <Backdrop>
      <Rise show>
        <span className="tw-mx-auto tw-mb-5 tw-grid tw-h-20 tw-w-20 tw-place-items-center tw-rounded-3xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-indigo-600 tw-text-white tw-shadow-2xl tw-shadow-teal-500/30">
          <LuBookOpen className="tw-h-10 tw-w-10" />
        </span>
        <p className="tw-text-5xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('player.title')}</p>
        <p className="tw-mt-3 tw-text-xl tw-text-slate-600 dark:tw-text-slate-300">{label('stage.introText')}</p>
      </Rise>
      <div className="tw-mt-10 tw-flex tw-w-[1120px] tw-items-start tw-gap-4">
        {groups.map((group, groupIndex) => (
          <Rise key={group.key} show={p > 0.12 + groupIndex * 0.12} className={group.key === 'profile' ? 'tw-flex-[2]' : 'tw-flex-1'}>
            <div className={`tw-p-4 tw-text-left ${CARD}`}>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-teal-700 dark:tw-text-teal-300">{label(`groups.${group.key}`)}</p>
              <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5">
                {group.chapters.map((key) => {
                  const Icon = CHAPTER_ICONS[key];
                  return (
                    <span key={key} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-semibold tw-text-slate-700 dark:tw-bg-white/10 dark:tw-text-slate-200">
                      <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600" />
                      {label(`chapters.${key}`)}
                    </span>
                  );
                })}
              </div>
            </div>
          </Rise>
        ))}
      </div>
    </Backdrop>
  );
};

TitleScene.propTypes = { state: PropTypes.object.isRequired, p: PropTypes.number.isRequired };

export default TitleScene;
